/**
 * GameEngine service — orchestrates engine sub-components per
 * /design/specs/4_architecture_c4_l3_components.md §2.3.
 *
 * V1 first-pass scope (this slice):
 *   - sampleHand(config, rng) → SampledHand (HandState ready for postflop play)
 *
 * Future additions:
 *   - applyAction wrapper (currently consumers call the state machine directly)
 *   - Villain decision orchestration via ArchetypeBehaviorDriver
 *   - Real ranges loaded from /reference/13 + /archetypes/14
 */

import type { Card } from '@/types/card';
import type { HandState } from '@/types/handState';
import type {
  PotType,
  Position,
  Archetype,
  SessionConfig,
} from '@/types/session';
import type { Range } from '@/types/range';
import type { RNG } from '@/lib/rng';
import { sampleHero, sampleVillain } from '@/lib/engine/handSampler';
import { sampleFlop, type FlopConstraint } from '@/lib/engine/boardSampler';
import { createFlopState } from '@/lib/engine/stateMachine';
import { parseRange } from '@/lib/engine/rangeParser';

// =============================================================================
// Engine defaults
// =============================================================================
//
// These will move into SessionConfig once we add blinds/stack to the form
// (deferred from the first vertical slice). For now the engine assumes the
// V1 PRD defaults.

const DEFAULT_BB = 5;
const DEFAULT_SB = 2;
const DEFAULT_STACK_BB = 200;
const SRP_PREFLOP_BB_PER_PLAYER = 3;     // hero opens 3, fish calls 3 — 6 BB total pot
const TBP_PREFLOP_BB_PER_PLAYER = 9;     // 3-bet to 9 BB, fish calls — 18 BB total pot

// =============================================================================
// Placeholder ranges
// =============================================================================
//
// V1 first-pass uses these hand-built minimal lists instead of /reference/13
// (hero RFI) and /archetypes/14 (Fish flat_vs_rfi / call_3b). When YAML loading
// is wired we swap these for the real ranges; the sampler's public interface
// doesn't change.

const PLACEHOLDER_HERO_RANGE: Range = parseRange(
  'AA,KK,QQ,JJ,TT,99,AKs,AQs,AJs,KQs,JTs,T9s,AKo,AQo,AJo',
);

const PLACEHOLDER_FISH_RANGE: Range = parseRange(
  'AA,KK,QQ,JJ,TT,99,88,77,66,55,AKs,AQs,AJs,ATs,KQs,KJs,QJs,JTs,T9s,98s,87s,76s,AKo,AQo,AJo,KQo,QJo,JTo',
);

// =============================================================================
// Public types
// =============================================================================

export interface SampledHand {
  /** Initial HandState at start of flop, ready for postflop action. */
  state: HandState;
  /**
   * The resolved configuration this hand was sampled under. Mirrors the
   * input config except `Any` values are replaced with concrete picks.
   */
  resolved: ResolvedConfig;
}

export interface ResolvedConfig {
  potType: Exclude<PotType, 'Any'>;
  position: Exclude<Position, 'Any'>;
  archetype: Exclude<Archetype, 'Any'>;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Sample a hand from the given session configuration. Pure function: same
 * (config, RNG seed) → same hand.
 */
export function sampleHand(config: SessionConfig, rng: RNG): SampledHand {
  const resolved = resolveConfig(config, rng);

  // Sample hero hole cards.
  const heroCombo = sampleHero(PLACEHOLDER_HERO_RANGE, [], rng);

  // Sample villain hole cards. Use Fish's range as a placeholder for every
  // archetype in V1 first-pass — Whale/TRec specs are pending.
  const villainCombo = sampleVillain(
    PLACEHOLDER_FISH_RANGE,
    [heroCombo[0], heroCombo[1]],
    rng,
  );

  // Sample flop respecting the (V1 first-pass: always 'Any') flop constraint.
  // When SessionConfig gains flop/turn/river constraint fields, plumb here.
  const flopConstraint: FlopConstraint = 'Any';
  const deadCards: Card[] = [
    heroCombo[0],
    heroCombo[1],
    villainCombo[0],
    villainCombo[1],
  ];
  const flopCards = sampleFlop(flopConstraint, deadCards, rng);

  // Pot + stacks: auto-resolve preflop.
  const bb = DEFAULT_BB;
  const startingStackChips = DEFAULT_STACK_BB * bb;
  const preflopPerPlayerBB =
    resolved.potType === 'SRP' ? SRP_PREFLOP_BB_PER_PLAYER : TBP_PREFLOP_BB_PER_PLAYER;
  const preflopPerPlayerChips = preflopPerPlayerBB * bb;
  const startingPot = preflopPerPlayerChips * 2;
  const startingStacks = {
    hero: startingStackChips - preflopPerPlayerChips,
    villain: startingStackChips - preflopPerPlayerChips,
  };

  const state = createFlopState({
    handId: makeHandId(rng),
    potType: resolved.potType,
    heroPosition: resolved.position,
    blinds: { sb: DEFAULT_SB, bb },
    heroCards: heroCombo,
    villainCards: villainCombo,
    flopCards,
    startingPot,
    startingStacks,
  });

  return { state, resolved };
}

// =============================================================================
// Helpers
// =============================================================================

function resolveConfig(config: SessionConfig, rng: RNG): ResolvedConfig {
  const potType: Exclude<PotType, 'Any'> =
    config.potType === 'Any' ? rng.pick(['SRP', '3BP'] as const) : config.potType;
  const position: Exclude<Position, 'Any'> =
    config.position === 'Any' ? rng.pick(['IP', 'OOP'] as const) : config.position;
  const archetype: Exclude<Archetype, 'Any'> =
    config.archetype === 'Any'
      ? rng.pick(['Fish', 'Whale', 'TRec'] as const)
      : config.archetype;
  return { potType, position, archetype };
}

function makeHandId(rng: RNG): string {
  // Lightweight ID — not a UUID. Sufficient for in-memory tracking.
  return `h-${rng.int(2 ** 30).toString(36)}`;
}
